import { useState } from 'react'
import { motion } from 'framer-motion'
import { Copy, Check } from 'lucide-react'

export const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      return true
    } catch (err) {
      console.error('Failed to copy:', err)
      return false
    }
  }

  return { copied, copyToClipboard }
}

export const CopyButton = ({ text, className = "" }) => {
  const { copied, copyToClipboard } = useCopyToClipboard()

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={() => copyToClipboard(text)}
      className={`p-2 hover:bg-gold-500/20 rounded-lg transition-colors relative ${className}`}
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-beige-400" />
      )}
      {copied && (
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs bg-green-500 text-white px-2 py-1 rounded whitespace-nowrap"
        >
          Copied!
        </motion.span>
      )}
    </motion.button>
  )
}

export const useKeyboardShortcuts = (shortcuts) => {
  useState(() => {
    const handleKeyPress = (e) => {
      shortcuts.forEach(({ key, ctrl, shift, alt, callback }) => {
        const ctrlMatch = ctrl ? (e.ctrlKey || e.metaKey) : true
        const shiftMatch = shift ? e.shiftKey : true
        const altMatch = alt ? e.altKey : true
        
        if (e.key === key && ctrlMatch && shiftMatch && altMatch) {
          e.preventDefault()
          callback()
        }
      })
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [shortcuts])
}

export const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('sq-AL', {
    style: 'currency',
    currency: currency
  }).format(amount)
}

export const formatDate = (date, format = 'short') => {
  const d = date instanceof Date ? date : new Date(date)
  
  if (format === 'short') {
    return d.toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  if (format === 'long') {
    return d.toLocaleDateString('sq-AL', { day: 'numeric', month: 'long', year: 'numeric' })
  }
  if (format === 'time') {
    return d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' })
  }
  if (format === 'datetime') {
    return `${formatDate(d, 'short')} ${formatDate(d, 'time')}`
  }
  return d.toLocaleDateString('sq-AL')
}

export const getTimeAgo = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  const now = new Date()
  const seconds = Math.floor((now - d) / 1000)

  if (seconds < 60) return 'Tani'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min më parë`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} orë më parë`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} ditë më parë`
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} javë më parë`
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} muaj më parë`
  return `${Math.floor(seconds / 31536000)} vjet më parë`
}

export const downloadFile = (data, filename, type = 'text/plain') => {
  const blob = new Blob([data], { type })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const truncateText = (text, maxLength = 100) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export const generateRandomColor = () => {
  const colors = [
    'from-red-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-green-500 to-emerald-500',
    'from-yellow-500 to-orange-500',
    'from-purple-500 to-pink-500',
    'from-indigo-500 to-blue-500',
    'from-gold-500 to-bronze-500',
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

export const validatePhone = (phone) => {
  const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/
  return re.test(phone)
}

// Export individual item as JSON
export const exportAsJSON = (data, filename) => {
  const jsonStr = JSON.stringify(data, null, 2)
  downloadFile(jsonStr, `${filename}.json`, 'application/json')
}

// Export individual item as CSV (single row)
export const exportAsCSV = (data, filename) => {
  const headers = Object.keys(data).join(',')
  const values = Object.values(data).map(v => 
    typeof v === 'string' && v.includes(',') ? `"${v}"` : v
  ).join(',')
  const csv = `${headers}\n${values}`
  downloadFile(csv, `${filename}.csv`, 'text/csv')
}

// Export individual item as formatted text
export const exportAsText = (data, filename, title) => {
  let text = `${title}\n${'='.repeat(title.length)}\n\n`
  Object.entries(data).forEach(([key, value]) => {
    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
    text += `${label}: ${value}\n`
  })
  text += `\n\nEksportuar: ${new Date().toLocaleString('sq-AL')}`
  downloadFile(text, `${filename}.txt`, 'text/plain')
}

// Print individual item
export const printItem = (data, title) => {
  const printWindow = window.open('', '_blank')
  let html = `
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          h1 { color: #C9A961; border-bottom: 2px solid #C9A961; padding-bottom: 10px; }
          .field { margin: 15px 0; }
          .label { font-weight: bold; color: #555; }
          .value { margin-left: 10px; }
          .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #888; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
  `
  
  Object.entries(data).forEach(([key, value]) => {
    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
    html += `<div class="field"><span class="label">${label}:</span><span class="value">${value || '-'}</span></div>`
  })
  
  html += `
        <div class="footer">
          Printuar: ${new Date().toLocaleString('sq-AL')}<br>
          Contenta Marketing Agency
        </div>
      </body>
    </html>
  `
  
  printWindow.document.write(html)
  printWindow.document.close()
  setTimeout(() => {
    printWindow.print()
  }, 250)
}
